import type {SiteLocale} from "seo/siteMeta";
import {localeCopyWithEnglishFallback} from "i18n/localeFallback";
import {hostedUploadLimitLabel} from "pricing/planEntitlements";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_DE} from "i18n/de/planLabels";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_EN} from "i18n/en/planLabels";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_FR, PLUS_UPLOAD as PLUS_UPLOAD_FR, PREMIUM_UPLOAD as PREMIUM_UPLOAD_FR} from "i18n/fr/planLabels";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_IT, PLUS_UPLOAD as PLUS_UPLOAD_IT, PREMIUM_UPLOAD as PREMIUM_UPLOAD_IT} from "i18n/it/planLabels";
import {INSTANT_FREE_SIZE as INSTANT_FREE_SIZE_RO} from "i18n/ro/planLabels";

const PLUS_UPLOAD_EN = hostedUploadLimitLabel("plus", "unlimited", "en");
const PREMIUM_UPLOAD_EN = hostedUploadLimitLabel("premium", "unlimited", "en");
const PLUS_UPLOAD_RO = hostedUploadLimitLabel("plus", "nelimitat", "ro");
const PREMIUM_UPLOAD_RO = hostedUploadLimitLabel("premium", "fără limită", "ro");
const PREMIUM_UPLOAD_SIZE_RO = hostedUploadLimitLabel("premium", "Nelimitată", "ro");
// German needs no second "unlimited" form beside these: "unbegrenzt" is invariant where Romanian has
// to choose between a masculine, a feminine and a plural.
const PLUS_UPLOAD_DE = hostedUploadLimitLabel("plus", "unbegrenzt", "de");
const PREMIUM_UPLOAD_DE = hostedUploadLimitLabel("premium", "unbegrenzt", "de");

/**
 * Per-page copy for the public **Client-Server (hosted / relay) transfer mode** page
 * (`/client-server`, issue ui#133).
 *
 * One of the TWO files that own this page (the other is {@link ../ClientServerContent.tsx}). Holds
 * every locale; the page selects one at runtime via `useLocale()`, so the `/ro` and `/de` SSG trees
 * render their own language for free. Routes, SEO wiring and the Resources link already exist - only
 * the strings below and the content component's markup carry the page.
 *
 * The shape below is intentionally richer than the bare `body: string[]` scaffold: the hosted mode is
 * best explained as a "how it works" walkthrough plus a side-by-side comparison with peer-to-peer, so
 * the copy is structured into named sections the dumb view renders as semantic HTML.
 */
export interface ClientServerCopy {
	/** `<Seo>` metadata - the browser/search title and meta description for this page. */
	seo: {
		/** Page title (the site name is appended automatically by `<Seo>`). */
		title: string;
		/** Meta description (~150-160 chars for a healthy search snippet). */
		description: string;
	};
	/** The single `<h1>` at the top of the page. */
	heading: string;
	/** One-line intro paragraph directly under the heading. */
	lede: string;
	/** Framing paragraphs under the lede - each renders as its own `<p>`. */
	intro: string[];
	/** "How a hosted transfer works" - an ordered walkthrough of the upload → link → download path. */
	how: {
		heading: string;
		/** Ordered steps; each renders as an `<li>` in an `<ol>`. */
		steps: string[];
	};
	/** Reliability highlights - an intro line plus a bullet list of titled points. */
	reliability: {
		heading: string;
		intro: string;
		items: {title: string; body: string}[];
	};
	/** Encryption section. `zk` is a sentence split around an inline link to `/zero-knowledge`. */
	encryption: {
		heading: string;
		body: string;
		zk: {before: string; link: string; after: string};
	};
	/** Side-by-side comparison table (client-server vs peer-to-peer). */
	compare: {
		heading: string;
		intro: string;
		/** Header for the leading row-label column. */
		featureLabel: string;
		/** Column headers for the two modes. */
		hostedLabel: string;
		p2pLabel: string;
		/** One row per compared aspect. */
		rows: {label: string; hosted: string; p2p: string}[];
	};
	/** "Which should you use?" closing help. `p2p` is a sentence split around a link to `/p2p`. */
	pick: {
		heading: string;
		hosted: string;
		p2p: {before: string; link: string; after: string};
		/**
		 * Links out to the industry landing pages this locale's content corpus ships, or `null`.
		 *
		 * Nullable rather than optional so every locale has to state its answer: the slugs are
		 * localized, and a locale inheriting English copy here would link addresses its own tree does
		 * not serve. English ships the two pages added by kitecorp/transferify-ui#2787, which is also
		 * what gives them a third inbound page for the content-reach rule; Romanian and German address
		 * their much larger industry corpus from their own pillar page instead.
		 */
		industries: {
			/** The sentence up to the first link. */
			lead: string;
			/** One entry per page: the anchor, and the text continuing the sentence after it. */
			links: {to: string; label: string; tail: string}[];
		} | null;
	};
}

/** EN + RO + DE + FR + IT copy, keyed by locale. `ClientServerContent` reads `clientServerCopy[locale]`. */
export const clientServerCopy: Record<SiteLocale, ClientServerCopy> = localeCopyWithEnglishFallback<ClientServerCopy>({
	en: {
		seo: {
			title: "Hosted (client-server) transfer, encrypted",
			description:
				"Transferify's hosted mode encrypts files on your device and stores them, so the recipient " +
				"can download later - even after you close the tab.",
		},
		heading: "Client-server transfer",
		lede:
			"Upload once, share a link, and let the recipient download whenever they're ready - even after " +
			"you close the tab. Every file is encrypted on your device before it leaves it.",
		intro: [
			"In client-server mode - the classic way most transfer tools work - your files are uploaded to " +
				"Transferify's storage first, then handed to the recipient through a download link. Because the " +
				"file lives on our servers, the transfer is asynchronous: you and the recipient never have to be " +
				"online at the same time.",
			"That's the key difference from a live peer-to-peer transfer. Hosted links cost you an upload wait " +
				"up front, but once it's done the link keeps working on its own - the recipient can grab the file " +
				"minutes or days later, from any device, long after your tab is closed.",
		],
		how: {
			heading: "How a hosted transfer works",
			steps: [
				"Your files are encrypted on your device with AES-256-GCM before a single byte leaves it, so " +
					"storage only ever receives encrypted data.",
				"The encrypted data is split into chunks and sent straight to storage as a multipart upload, so a " +
					"large file goes up as many independent parts instead of one fragile stream. The chunk size is " +
					"chosen per file - 8 MiB for everyday transfers, scaling up as the file grows - so even the " +
					"largest upload still fits the 10,000-part limit.",
				"Any part that fails is retried automatically with exponential backoff, and its upload URL is " +
					"re-signed on every attempt - so a brief network drop doesn't cost you the whole upload.",
				"Once the upload finishes, your share link goes live. The recipient opens it and the file streams " +
					"down chunk by chunk, decrypted on the fly in their browser - no account, nothing to install.",
			],
		},
		reliability: {
			heading: "Built for big files and shaky networks",
			intro: "Holding the file server-side is what makes this mode dependable:",
			items: [
				{
					title: "Chunked, multipart upload",
					body:
						"A large file is sliced into parts and uploaded independently, so throughput stays high and a " +
						"single dropped part never restarts the whole transfer. Part size grows with the file, so even " +
						"the biggest upload we accept still fits inside the same 10,000-part ceiling.",
				},
				{
					title: "Automatic retries",
					body:
						"When the connection drops, a part is retried for up to ten minutes, with pauses growing from " +
						"half a second to thirty seconds and a freshly signed URL each time. Time spent offline doesn't " +
						"count toward those ten minutes, so a multi-hour upload over unreliable Wi-Fi still finishes.",
				},
				{
					title: "Large files",
					body:
						`Start free, then send up to ${PLUS_UPLOAD_EN} on Plus and ${PREMIUM_UPLOAD_EN} on Premium - the multipart engine carries ` +
						"the size.",
				},
				{
					title: "Streamed, memory-safe downloads",
					body:
						"The recipient's browser pulls the file one chunk at a time and writes it straight to disk, so " +
						"even huge files never have to fit in memory. Each new download counts toward the transfer's " +
						"download limit and the sender's monthly allowance, and is refused once either is used up.",
				},
			],
		},
		encryption: {
			heading: "Encrypted before it leaves your device",
			body:
				"Hosted doesn't mean exposed. Your files are encrypted on your device with AES-256-GCM before " +
				"they're uploaded, so our storage only ever holds encrypted files. Every chunk is sealed separately " +
				"and bound to its position in the file, so the download fails loudly if a single byte is tampered " +
				"with or reordered.",
			zk: {
				before:
					"By default we keep the file key, wrapped by a key held in AWS KMS, and our server unwraps it " +
					"to hand it to the people who open the link. That is what lets the link work without carrying " +
					"the key, and what keeps a lost link recoverable. Switch on Zero-Knowledge mode and the key " +
					"stays in the link, after the “#”, so it never reaches us and we cannot read those files. ",
				link: "See how zero-knowledge encryption works",
				after: ".",
			},
		},
		compare: {
			heading: "Client-server vs peer-to-peer",
			intro:
				"Two ways to send the same file. Client-server stores it so it can wait; peer-to-peer streams it " +
				"live, device to device, and stores nothing.",
			featureLabel: "Aspect",
			hostedLabel: "Client-server",
			p2pLabel: "Peer-to-peer",
			rows: [
				{label: "When the link works", hosted: "As soon as the upload finishes", p2p: "Instantly, before any upload"},
				{label: "Path the file takes", hosted: "Device → server → device", p2p: "Device → device, directly"},
				{label: "Recipient", hosted: "Downloads anytime, even days later", p2p: "Must be online while you send"},
				{label: "When you close your tab", hosted: "Link stays online", p2p: "Transfer ends"},
				{label: "Where the file lives", hosted: "Encrypted on our servers until it expires", p2p: "Nowhere - never stored"},
				{label: "File size", hosted: `Free to start · ${PLUS_UPLOAD_EN} Plus · ${PREMIUM_UPLOAD_EN} Premium`, p2p: `Free up to ${INSTANT_FREE_SIZE_EN} · any size on a paid plan`},
			],
		},
		pick: {
			heading: "Which should you use?",
			hosted:
				"Choose client-server when the recipient may be offline, when you want the link to keep working " +
				"after you close your tab, or when you need passwords, expiry dates and download limits on the file.",
			p2p: {
				before: "Choose ",
				link: "peer-to-peer",
				after:
					" when you're both online and want the file to stream instantly, at full speed, with nothing " +
					"ever stored on a server.",
			},
			industries: {
				lead: "Sending a set of drawings from a site office, or a season catalogue to a printer? The page for ",
				links: [
					{
						to: "/for/construction-and-trades",
						label: "construction and trades",
						tail: " walks through drawings, quotes and site photos, and the page for ",
					},
					{
						to: "/for/retail-and-manufacturing",
						label: "retail and manufacturing",
						tail: " does the same for catalogues, product photos and print-ready files.",
					},
				],
			},
		},
	},
	ro: {
		seo: {
			title: "Transfer găzduit client-server, criptat",
			description:
				"Modul găzduit criptează fișierele pe dispozitivul tău și le stochează, așa că destinatarul " +
				"le poate descărca și după ce închizi fila.",
		},
		heading: "Transfer client-server",
		lede:
			"Încarci o singură dată, partajezi un link, iar destinatarul descarcă atunci când e pregătit - " +
			"chiar și după ce închizi fila. Fiecare fișier este criptat pe dispozitivul tău înainte să îl părăsească.",
		intro: [
			"În modul client-server - varianta clasică, așa cum funcționează majoritatea instrumentelor de " +
				"transfer - fișierele tale sunt încărcate mai întâi în stocarea Transferify, apoi predate " +
				"destinatarului printr-un link de descărcare. Fiindcă fișierul stă pe serverele noastre, transferul " +
				"este asincron: tu și destinatarul nu trebuie să fiți online în același timp.",
			"Aceasta este diferența esențială față de un transfer peer-to-peer live. Linkurile găzduite îți cer " +
				"o așteptare la încărcare la început, dar odată terminată, linkul funcționează singur - destinatarul " +
				"poate lua fișierul peste câteva minute sau peste câteva zile, de pe orice dispozitiv, mult după ce " +
				"fila ta s-a închis.",
		],
		how: {
			heading: "Cum funcționează un transfer găzduit",
			steps: [
				"Fișierele tale sunt criptate pe dispozitiv cu AES-256-GCM înainte ca vreun octet să îl părăsească, " +
					"așa că în stocare ajung doar date criptate.",
				"Datele criptate sunt împărțite în bucăți și trimise direct în stocare printr-o încărcare multipart, " +
					"așa că un fișier mare urcă în multe segmente independente, nu într-un singur flux fragil. " +
					"Dimensiunea bucății este aleasă pentru fiecare fișier - 8 MiB pentru transferurile obișnuite și " +
					"mai mare pe măsură ce fișierul crește - astfel încât și cea mai mare încărcare încape în limita " +
					"de 10.000 de segmente.",
				"Orice segment care eșuează este reîncercat automat, cu pauze care se dublează, iar URL-ul său de " +
					"încărcare este re-semnat la fiecare încercare - așa că o scurtă întrerupere de rețea nu te costă " +
					"toată încărcarea.",
				"Când încărcarea se termină, linkul de partajare devine activ. Destinatarul îl deschide, iar fișierul " +
					"se descarcă bucată cu bucată, decriptate din mers în browserul lui - fără cont, fără nimic de instalat.",
			],
		},
		reliability: {
			heading: "Făcut pentru fișiere mari și conexiuni instabile",
			intro: "Păstrarea fișierului pe server este ceea ce face acest mod fiabil:",
			items: [
				{
					title: "Încărcare multipart, în bucăți",
					body:
						"Un fișier mare este împărțit în segmente și încărcat independent, așa că viteza rămâne ridicată, " +
						"iar un singur segment pierdut nu repornește tot transferul. Dimensiunea segmentului crește " +
						"odată cu fișierul, așa că și cea mai mare încărcare pe care o acceptăm încape în aceeași " +
						"limită de 10.000 de segmente.",
				},
				{
					title: "Reîncercări automate",
					body:
						"Când conexiunea cade, un segment este reîncercat timp de până la zece minute, cu pauze care cresc " +
						"de la o jumătate de secundă la treizeci de secunde și un URL proaspăt semnat de fiecare dată. " +
						"Timpul petrecut offline nu intră în cele zece minute, așa că o încărcare de câteva ore, pe un " +
						"Wi-Fi nesigur, tot se finalizează.",
				},
				{
					title: "Fișiere mari",
					body:
						`Începi gratuit, apoi trimiți până la ${PLUS_UPLOAD_RO} pe Plus și ${PREMIUM_UPLOAD_RO} pe Premium - motorul multipart duce ` +
						"dimensiunea.",
				},
				{
					title: "Descărcări în flux, sigure pentru memorie",
					body:
						"Browserul destinatarului aduce fișierul bucată cu bucată și îl scrie direct pe disc, așa că " +
						"nici fișierele uriașe nu trebuie să încapă în memorie. Fiecare descărcare nouă intră în " +
						"limita de descărcări a transferului și în cota lunară a expeditorului și este refuzată când " +
						"oricare dintre ele s-a epuizat.",
				},
			],
		},
		encryption: {
			heading: "Criptat înainte să plece de pe dispozitivul tău",
			body:
				"Găzduit nu înseamnă expus. Fișierele tale sunt criptate pe dispozitiv cu AES-256-GCM înainte de a " +
				"fi încărcate, așa că stocarea noastră păstrează doar fișiere criptate. Fiecare bucată este sigilată " +
				"separat și legată de poziția ei în fișier, așa că descărcarea eșuează zgomotos dacă un singur octet " +
				"este alterat sau reordonat.",
			zk: {
				before:
					"În mod implicit păstrăm cheia fișierului, împachetată cu o cheie ținută în AWS KMS, iar serverul " +
					"nostru o despachetează ca s-o predea celor care deschid linkul. Asta permite linkului să " +
					"funcționeze fără să conțină cheia și face ca un link pierdut să rămână recuperabil. Activează " +
					"modul Zero-Knowledge și cheia rămâne în link, după „#”, așa că nu ajunge niciodată la noi și nu " +
					"putem citi acele fișiere. ",
				link: "Vezi cum funcționează criptarea zero-knowledge",
				after: ".",
			},
		},
		compare: {
			heading: "Client-server vs. peer-to-peer",
			intro:
				"Două moduri de a trimite același fișier. Client-server îl stochează ca să poată aștepta; " +
				"peer-to-peer îl transmite live, de la un dispozitiv la altul, și nu stochează nimic.",
			featureLabel: "Aspect",
			hostedLabel: "Client-server",
			p2pLabel: "Peer-to-peer",
			rows: [
				{label: "Când funcționează linkul", hosted: "Imediat ce se termină încărcarea", p2p: "Instant, înainte de orice încărcare"},
				{label: "Traseul fișierului", hosted: "Dispozitiv → server → dispozitiv", p2p: "Dispozitiv → dispozitiv, direct"},
				{label: "Destinatar", hosted: "Descarcă oricând, chiar și peste zile", p2p: "Trebuie să fie online în timp ce trimiți"},
				{label: "Când închizi fila", hosted: "Linkul rămâne activ", p2p: "Transferul se oprește"},
				{label: "Unde stă fișierul", hosted: "Criptat pe serverele noastre până expiră", p2p: "Nicăieri - niciodată stocat"},
				{label: "Dimensiunea fișierului", hosted: `Gratuit la start · ${PLUS_UPLOAD_RO} Plus · ${PREMIUM_UPLOAD_SIZE_RO} Premium`, p2p: `Gratuit până la ${INSTANT_FREE_SIZE_RO} · orice dimensiune cu un plan plătit`},
			],
		},
		pick: {
			heading: "Pe care să îl folosești?",
			hosted:
				"Alege client-server când destinatarul ar putea fi offline, când vrei ca linkul să funcționeze și " +
				"după ce închizi fila sau când ai nevoie de parole, date de expirare și limite de descărcare pentru fișier.",
			p2p: {
				before: "Alege ",
				link: "peer-to-peer",
				after:
					" când sunteți amândoi online și vrei ca fișierul să circule instant, la viteză maximă, fără ca " +
					"nimic să fie stocat vreodată pe server.",
			},
			industries: null,
		},
	},
	de: {
		seo: {
			// 60 characters INCLUDING the " | Transferify" that `<Seo>` appends, which the SSG head guard
			// enforces at build time: "Gehosteter Client-Server-Transfer, verschlüsselt" came to 62.
			title: "Client-Server-Transfer, verschlüsselt",
			description:
				"Der gehostete Modus verschlüsselt Dateien auf deinem Gerät und speichert sie, sodass der " +
				"Empfänger auch nach dem Schließen deines Tabs herunterladen kann.",
		},
		heading: "Client-Server-Transfer",
		lede:
			"Einmal hochladen, einen Link teilen, und der Empfänger lädt herunter, wann er so weit ist - auch " +
			"nachdem du den Tab geschlossen hast. Jede Datei wird auf deinem Gerät verschlüsselt, bevor sie es verlässt.",
		intro: [
			"Im Client-Server-Modus - dem klassischen Weg, so wie die meisten Transfer-Werkzeuge arbeiten - " +
				"werden deine Dateien zuerst in den Speicher von Transferify hochgeladen und dem Empfänger dann " +
				"über einen Download-Link übergeben. Weil die Datei auf unseren Servern liegt, läuft der Transfer " +
				"asynchron: Du und der Empfänger müsst nie gleichzeitig online sein.",
			"Das ist der entscheidende Unterschied zu einem Peer-to-Peer-Transfer in Echtzeit. Gehostete Links " +
				"kosten dich vorab eine Wartezeit beim Hochladen, aber danach funktioniert der Link von allein - " +
				"der Empfänger holt die Datei Minuten oder Tage später, von jedem Gerät, lange nachdem dein Tab " +
				"zu ist.",
		],
		how: {
			heading: "So läuft ein gehosteter Transfer ab",
			steps: [
				"Deine Dateien werden auf deinem Gerät mit AES-256-GCM verschlüsselt, bevor ein einziges Byte es " +
					"verlässt, im Speicher kommen also nur verschlüsselte Daten an.",
				"Die verschlüsselten Daten werden in Stücke geteilt und als Multipart-Upload direkt in den Speicher " +
					"geschickt, sodass eine große Datei in vielen unabhängigen Teilen hochgeht statt in einem " +
					"anfälligen Strom. Die Stückgröße wird pro Datei gewählt - 8 MiB für alltägliche Transfers, " +
					"größer, je größer die Datei wird - damit auch der größte Upload noch in die Grenze von 10.000 " +
					"Teilen passt.",
				"Jedes Teil, das fehlschlägt, wird automatisch wiederholt, mit exponentiell wachsenden Pausen, und " +
					"seine Upload-URL wird bei jedem Versuch neu signiert - eine kurze Netzstörung kostet dich also " +
					"nicht den ganzen Upload.",
				"Sobald der Upload fertig ist, geht dein Freigabelink live. Der Empfänger öffnet ihn, und die Datei " +
					"läuft Stück für Stück herunter und wird im Browser direkt entschlüsselt - ohne Konto, ohne " +
					"etwas zu installieren.",
			],
		},
		reliability: {
			heading: "Gebaut für große Dateien und wackelige Netze",
			intro: "Dass die Datei auf dem Server liegt, macht diesen Modus verlässlich:",
			items: [
				{
					title: "Multipart-Upload in Stücken",
					body:
						"Eine große Datei wird in Teile geschnitten und unabhängig hochgeladen, sodass der Durchsatz hoch " +
						"bleibt und ein einzelnes verlorenes Teil nie den ganzen Transfer neu startet. Die Teilgröße " +
						"wächst mit der Datei, damit auch der größte Upload, den wir annehmen, in dieselbe Grenze von " +
						"10.000 Teilen passt.",
				},
				{
					title: "Automatische Wiederholungen",
					body:
						"Bricht die Verbindung ab, wird ein Teil bis zu zehn Minuten lang wiederholt, mit Pausen, die " +
						"von einer halben Sekunde auf dreißig Sekunden wachsen, und jedes Mal mit einer frisch " +
						"signierten URL. Zeit ohne Verbindung zählt nicht zu diesen zehn Minuten, sodass auch ein " +
						"mehrstündiger Upload über unzuverlässiges WLAN fertig wird.",
				},
				{
					title: "Große Dateien",
					body:
						`Fang kostenlos an, sende dann bis zu ${PLUS_UPLOAD_DE} auf Plus und ${PREMIUM_UPLOAD_DE} auf Premium - die Multipart-Maschinerie ` +
						"trägt die Größe.",
				},
				{
					title: "Gestreamte, speicherschonende Downloads",
					body:
						"Der Browser des Empfängers holt die Datei Stück für Stück und schreibt sie direkt auf die " +
						"Platte, sodass auch riesige Dateien nie in den Arbeitsspeicher passen müssen. Jeder neue " +
						"Download zählt zum Download-Limit des Transfers und zum Monatskontingent des Absenders und wird " +
						"abgelehnt, sobald eines von beiden aufgebraucht ist.",
				},
			],
		},
		encryption: {
			heading: "Schon auf deinem Gerät verschlüsselt",
			body:
				"Gehostet heißt nicht offen. Deine Dateien werden auf deinem Gerät mit AES-256-GCM verschlüsselt, " +
				"bevor sie hochgeladen werden, unser Speicher enthält also nur verschlüsselte Dateien. Jedes Stück " +
				"wird einzeln versiegelt und an seine Position in der Datei gebunden, sodass der Download laut " +
				"scheitert, wenn ein einziges Byte verändert oder umgestellt wurde.",
			zk: {
				before:
					"Standardmäßig hinterlegen wir den Dateischlüssel, eingepackt von einem Schlüssel in AWS KMS, " +
					"und unser Server packt ihn aus, um ihn den Personen zu geben, die den Link öffnen. Deshalb " +
					"funktioniert der Link, ohne den Schlüssel zu enthalten, und ein verlorener Link bleibt " +
					"wiederherstellbar. Schalte den Zero-Knowledge-Modus ein, und der Schlüssel bleibt im Link, " +
					"nach dem „#“, er erreicht uns also nie, und wir können diese Dateien nicht lesen. ",
				link: "Sieh dir an, wie Zero-Knowledge-Verschlüsselung funktioniert",
				after: ".",
			},
		},
		compare: {
			heading: "Client-Server gegen Peer-to-Peer",
			intro:
				"Zwei Wege, dieselbe Datei zu senden. Client-Server speichert sie, damit sie warten kann; " +
				"Peer-to-Peer überträgt sie live, von Gerät zu Gerät, und speichert nichts.",
			featureLabel: "Aspekt",
			hostedLabel: "Client-Server",
			p2pLabel: "Peer-to-Peer",
			rows: [
				{label: "Ab wann der Link geht", hosted: "Sobald der Upload fertig ist", p2p: "Sofort, vor jedem Upload"},
				{label: "Weg der Datei", hosted: "Gerät → Server → Gerät", p2p: "Gerät → Gerät, direkt"},
				{label: "Empfänger", hosted: "Lädt jederzeit herunter, auch Tage später", p2p: "Muss online sein, während du sendest"},
				{label: "Wenn du deinen Tab schließt", hosted: "Link bleibt online", p2p: "Transfer endet"},
				{label: "Wo die Datei liegt", hosted: "Verschlüsselt auf unseren Servern bis zum Ablauf", p2p: "Nirgends - nie gespeichert"},
				{label: "Dateigröße", hosted: `Kostenlos starten · ${PLUS_UPLOAD_DE} Plus · ${PREMIUM_UPLOAD_DE} Premium`, p2p: `Bis ${INSTANT_FREE_SIZE_DE} kostenlos · jede Größe mit einem bezahlten Plan`},
			],
		},
		pick: {
			heading: "Welchen solltest du nehmen?",
			hosted:
				"Nimm Client-Server, wenn der Empfänger offline sein könnte, wenn der Link auch nach dem Schließen " +
				"deines Tabs weiter funktionieren soll, oder wenn du Passwörter, Ablaufdaten und Download-Limits " +
				"für die Datei brauchst.",
			p2p: {
				before: "Nimm ",
				link: "Peer-to-Peer",
				after:
					", wenn ihr beide online seid und die Datei sofort und mit voller Geschwindigkeit laufen soll, " +
					"ohne dass je etwas auf einem Server gespeichert wird.",
			},
			industries: null,
		},
	},
	fr: {
		seo: {
			title: "Transfert hébergé (client-serveur), chiffré",
			description:
				"Le mode hébergé de Transferify chiffre vos fichiers sur votre appareil et les stocke\u00a0: le destinataire " +
				"télécharge quand il veut, même onglet fermé.",
		},
		heading: "Transfert client-serveur",
		lede:
			"Envoyez une fois, partagez un lien, et laissez le destinataire télécharger quand il est prêt, même " +
			"après la fermeture de l’onglet. Chaque fichier est chiffré sur votre appareil avant de le quitter.",
		intro: [
			"En mode client-serveur, la méthode classique de la plupart des outils de transfert, vos fichiers sont " +
				"d’abord envoyés vers le stockage de Transferify, puis remis au destinataire par un lien de " +
				"téléchargement. Comme le fichier se trouve sur nos serveurs, le transfert est asynchrone\u00a0: vous et " +
				"le destinataire n’avez jamais besoin d’être en ligne en même temps.",
			"C’est la différence essentielle avec un transfert pair à pair en direct. Les liens hébergés demandent " +
				"d’abord un temps d’envoi, mais une fois celui-ci terminé, le lien fonctionne tout seul\u00a0: le " +
				"destinataire peut récupérer le fichier quelques minutes ou plusieurs jours plus tard, depuis " +
				"n’importe quel appareil, bien après la fermeture de votre onglet.",
		],
		how: {
			heading: "Comment fonctionne un transfert hébergé",
			steps: [
				"Vos fichiers sont chiffrés sur votre appareil avec AES-256-GCM avant qu’un seul octet ne le quitte\u00a0: " +
					"le stockage ne reçoit donc que des données chiffrées.",
				"Les données chiffrées sont découpées en blocs et envoyées directement vers le stockage en envoi " +
					"fractionné (multipart)\u00a0: un gros fichier part en de nombreuses parties indépendantes plutôt " +
					"qu’en un seul flux fragile. La taille des blocs est choisie fichier par fichier (8\u00a0Mio pour " +
					"les transferts courants, de plus en plus grands à mesure que le fichier grossit), si bien que " +
					"même le plus gros envoi respecte la limite de 10\u202f000 parties.",
				"Toute partie qui échoue est retentée automatiquement avec un délai croissant, et son URL d’envoi " +
					"est signée de nouveau à chaque tentative\u00a0: une brève coupure réseau ne vous coûte donc pas " +
					"tout l’envoi.",
				"Une fois l’envoi terminé, votre lien de partage est actif. Le destinataire l’ouvre et le fichier " +
					"arrive bloc par bloc, déchiffré à la volée dans son navigateur\u00a0: sans compte, sans rien à installer.",
			],
		},
		reliability: {
			heading: "Conçu pour les gros fichiers et les réseaux instables",
			intro: "Conserver le fichier côté serveur est ce qui rend ce mode fiable\u00a0:",
			items: [
				{
					title: "Envoi fractionné par blocs",
					body:
						"Un gros fichier est découpé en parties envoyées indépendamment\u00a0: le débit reste élevé et une " +
						"partie perdue ne relance jamais tout le transfert. La taille des parties grandit avec le " +
						"fichier, si bien que même le plus gros envoi que nous acceptons reste sous le même plafond de " +
						"10\u202f000 parties.",
				},
				{
					title: "Nouvelles tentatives automatiques",
					body:
						"Quand la connexion tombe, une partie est retentée pendant dix minutes au plus, avec des pauses " +
						"qui passent d’une demi-seconde à trente secondes et une URL fraîchement signée à chaque fois. " +
						"Le temps passé hors ligne ne compte pas dans ces dix minutes\u00a0: un envoi de plusieurs heures " +
						"sur un Wi-Fi peu fiable aboutit quand même.",
				},
				{
					title: "Gros fichiers",
					body:
						`Commencez gratuitement, puis envoyez jusqu’à ${PLUS_UPLOAD_FR} avec Plus et ${PREMIUM_UPLOAD_FR} avec Premium\u00a0: ` +
						"le moteur multipart absorbe la taille.",
				},
				{
					title: "Téléchargements en flux, sans saturer la mémoire",
					body:
						"Le navigateur du destinataire récupère le fichier bloc par bloc et l’écrit directement sur le " +
						"disque\u00a0: même un fichier énorme n’a jamais besoin de tenir en mémoire. Chaque nouveau " +
						"téléchargement compte dans la limite de téléchargements du transfert et dans le quota mensuel " +
						"de l’expéditeur, et il est refusé une fois l’un des deux épuisé.",
				},
			],
		},
		encryption: {
			heading: "Chiffré avant de quitter votre appareil",
			body:
				"Hébergé ne veut pas dire exposé. Vos fichiers sont chiffrés sur votre appareil avec AES-256-GCM " +
				"avant d’être envoyés\u00a0: notre stockage ne contient donc que des fichiers chiffrés. Chaque bloc est " +
				"scellé séparément et lié à sa position dans le fichier\u00a0: le téléchargement échoue bruyamment si un " +
				"seul octet est altéré ou déplacé.",
			zk: {
				before:
					"Par défaut, nous conservons la clé du fichier, protégée par une clé détenue dans AWS KMS, et notre " +
					"serveur la déverrouille pour la remettre aux personnes qui ouvrent le lien. C’est ce qui permet au " +
					"lien de fonctionner sans porter la clé, et ce qui garde un lien perdu récupérable. Activez le mode " +
					"Zéro connaissance et la clé reste dans le lien, après le «\u00a0#\u00a0»\u00a0: elle ne nous parvient jamais et " +
					"nous ne pouvons pas lire ces fichiers. ",
				link: "Voir comment fonctionne le chiffrement Zéro connaissance",
				after: ".",
			},
		},
		compare: {
			heading: "Client-serveur et pair à pair",
			intro:
				"Deux façons d’envoyer le même fichier. Le client-serveur le stocke pour qu’il puisse attendre\u00a0; le " +
				"pair à pair le diffuse en direct, d’un appareil à l’autre, sans rien stocker.",
			featureLabel: "Critère",
			hostedLabel: "Client-serveur",
			p2pLabel: "Pair à pair",
			rows: [
				{label: "Quand le lien fonctionne", hosted: "Dès la fin de l’envoi", p2p: "Immédiatement, avant tout envoi"},
				{label: "Chemin du fichier", hosted: "Appareil → serveur → appareil", p2p: "Appareil → appareil, en direct"},
				{label: "Destinataire", hosted: "Télécharge à tout moment, même des jours plus tard", p2p: "Doit être en ligne pendant votre envoi"},
				{label: "Quand vous fermez l’onglet", hosted: "Le lien reste en ligne", p2p: "Le transfert s’arrête"},
				{label: "Où se trouve le fichier", hosted: "Chiffré sur nos serveurs jusqu’à son expiration", p2p: "Nulle part\u00a0: jamais stocké"},
				{label: "Taille des fichiers", hosted: `Gratuit pour commencer · ${PLUS_UPLOAD_FR} avec Plus · ${PREMIUM_UPLOAD_FR} avec Premium`, p2p: `Gratuit jusqu’à ${INSTANT_FREE_SIZE_FR} · toute taille avec un plan payant`},
			],
		},
		pick: {
			heading: "Lequel choisir\u00a0?",
			hosted:
				"Choisissez le client-serveur quand le destinataire peut être hors ligne, quand vous voulez que le lien " +
				"continue de fonctionner après la fermeture de votre onglet, ou quand vous avez besoin de mots de passe, " +
				"de dates d’expiration et de limites de téléchargement sur le fichier.",
			p2p: {
				before: "Choisissez le ",
				link: "pair à pair",
				after:
					" quand vous êtes tous deux en ligne et voulez que le fichier circule instantanément, à pleine " +
					"vitesse, sans que rien ne soit jamais stocké sur un serveur.",
			},
			/* French addresses its industry corpus from its own pillar page, as Romanian and German do; English alone links two of them here. */
			industries: null,
		},
	},
	it: {
		seo: {
			title: "Trasferimento client-server, cifrato",
			description:
				"La modalità ospitata di Transferify cifra i file sul dispositivo e li conserva: il destinatario " +
				"scarica più tardi, anche a scheda chiusa.",
		},
		heading: "Trasferimento client-server",
		lede:
			"Carichi una sola volta, condivida un link e lasci che il destinatario scarichi quando vuole, anche a " +
			"scheda chiusa. Ogni file viene cifrato sul dispositivo prima di lasciarlo.",
		intro: [
			"Nella modalità client-server, il modo classico in cui funzionano la maggior parte degli strumenti di " +
				"trasferimento, i file vengono caricati prima nell’archiviazione di Transferify e poi consegnati al " +
				"destinatario tramite un link di download. Poiché il file risiede sui nostri server, il trasferimento " +
				"è asincrono: mittente e destinatario non devono mai essere online nello stesso momento.",
			"È questa la differenza fondamentale rispetto a un trasferimento peer-to-peer in tempo reale. I link " +
				"ospitati richiedono all’inizio l’attesa del caricamento, ma una volta terminato il link continua a " +
				"funzionare da solo: il destinatario può scaricare il file dopo pochi minuti o dopo giorni, da " +
				"qualsiasi dispositivo, molto tempo dopo la chiusura della scheda.",
		],
		how: {
			heading: "Come funziona un trasferimento ospitato",
			steps: [
				"I file vengono cifrati sul dispositivo con AES-256-GCM prima che un solo byte lo lasci, quindi " +
					"all’archiviazione arrivano esclusivamente dati cifrati.",
				"I dati cifrati vengono suddivisi in blocchi e inviati direttamente all’archiviazione come " +
					"caricamento multipart, così un file di grandi dimensioni parte in molte parti indipendenti " +
					"invece che in un unico flusso fragile. La dimensione dei blocchi viene scelta per ogni file " +
					"(8\u00a0MiB per i trasferimenti di uso comune, in aumento man mano che il file cresce), perciò " +
					"anche il caricamento più grande rientra nel limite di 10.000 parti.",
				"Ogni parte che fallisce viene ritentata automaticamente, con pause che crescono in modo " +
					"esponenziale, e il suo URL di caricamento viene firmato di nuovo a ogni tentativo: un breve " +
					"calo della rete non fa quindi perdere l’intero caricamento.",
				"Terminato il caricamento, il link di condivisione diventa attivo. Il destinatario lo apre e il " +
					"file arriva blocco dopo blocco, decifrato al volo direttamente nel browser: senza " +
					"account, senza nulla da installare.",
			],
		},
		reliability: {
			heading: "Pensato per file grandi e reti instabili",
			intro: "È la conservazione del file sul server a rendere affidabile questa modalità:",
			items: [
				{
					title: "Caricamento multipart a blocchi",
					body:
						"Un file di grandi dimensioni viene suddiviso in parti e caricato in modo indipendente, così " +
						"la velocità resta alta e una singola parte persa non fa mai ripartire l’intero " +
						"trasferimento. La dimensione delle parti cresce con il file, quindi anche il caricamento più " +
						"grande che accettiamo rientra nello stesso limite di 10.000 parti.",
				},
				{
					title: "Nuovi tentativi automatici",
					body:
						"Quando la connessione cade, una parte viene ritentata per un massimo di dieci minuti, con " +
						"pause che crescono da mezzo secondo a trenta secondi e un URL appena firmato a ogni " +
						"tentativo. Il tempo trascorso offline non rientra in quei dieci minuti, perciò anche un " +
						"caricamento di molte ore su un Wi-Fi poco affidabile arriva comunque a termine.",
				},
				{
					title: "File di grandi dimensioni",
					body:
						`Si parte gratis, poi si inviano fino a ${PLUS_UPLOAD_IT} con Plus e ${PREMIUM_UPLOAD_IT} con Premium: ` +
						"le dimensioni le gestisce il motore multipart.",
				},
				{
					title: "Download in streaming, senza pesare sulla memoria",
					body:
						"Il browser del destinatario recupera il file un blocco alla volta e lo scrive direttamente " +
						"su disco, perciò nemmeno i file enormi devono entrare in memoria. Ogni nuovo download viene " +
						"conteggiato nel limite di download del trasferimento e nella quota mensile del mittente, e " +
						"viene rifiutato quando uno dei due è esaurito.",
				},
			],
		},
		encryption: {
			heading: "Cifrato prima di lasciare il dispositivo",
			body:
				"Ospitato non significa esposto. I file vengono cifrati sul dispositivo con AES-256-GCM prima del " +
				"caricamento, quindi la nostra archiviazione contiene soltanto file cifrati. Ogni blocco è " +
				"sigillato separatamente e legato alla propria posizione nel file, perciò il download fallisce in " +
				"modo evidente se anche un solo byte viene manomesso o riordinato.",
			zk: {
				before:
					"Per impostazione predefinita conserviamo la chiave del file, protetta da una chiave custodita in " +
					"AWS KMS, e il nostro server la sblocca per consegnarla a chi apre il link. È ciò che permette al " +
					"link di funzionare senza contenere la chiave e che rende recuperabile un link smarrito. Attivando " +
					"la modalità Zero-Knowledge la chiave resta nel link, dopo il «#»: non ci raggiunge mai e non " +
					"possiamo leggere quei file. ",
				link: "Scopra come funziona la crittografia zero-knowledge",
				after: ".",
			},
		},
		compare: {
			heading: "Client-server e peer-to-peer a confronto",
			intro:
				"Due modi per inviare lo stesso file. Il client-server lo conserva, così può aspettare; il " +
				"peer-to-peer lo trasmette in tempo reale, da dispositivo a dispositivo, e non conserva nulla.",
			featureLabel: "Aspetto",
			hostedLabel: "Modalità client-server",
			p2pLabel: "Modalità peer-to-peer",
			rows: [
				{label: "Quando funziona il link", hosted: "Appena termina il caricamento", p2p: "Subito, prima di qualsiasi caricamento"},
				{label: "Percorso del file", hosted: "Dispositivo → server → dispositivo", p2p: "Dispositivo → dispositivo, in modo diretto"},
				{label: "Destinatario", hosted: "Scarica quando vuole, anche dopo giorni", p2p: "Deve essere online mentre si invia"},
				{label: "Quando si chiude la scheda", hosted: "Il link resta online", p2p: "Il trasferimento si interrompe"},
				{label: "Dove si trova il file", hosted: "Cifrato sui nostri server fino alla scadenza", p2p: "Da nessuna parte: non viene mai conservato"},
				{label: "Dimensione del file", hosted: `Gratis per iniziare · ${PLUS_UPLOAD_IT} Plus · ${PREMIUM_UPLOAD_IT} Premium`, p2p: `Gratis fino a ${INSTANT_FREE_SIZE_IT} · qualsiasi dimensione con un piano a pagamento`},
			],
		},
		pick: {
			heading: "Quale scegliere?",
			hosted:
				"Scegliere il client-server quando il destinatario potrebbe essere offline, quando si vuole che il " +
				"link continui a funzionare dopo la chiusura della scheda, oppure quando servono password, date di " +
				"scadenza e limiti di download per il file.",
			p2p: {
				before: "Scegliere il ",
				link: "trasferimento peer-to-peer",
				after:
					" quando entrambe le parti sono online e si vuole che il file scorra all’istante, a piena " +
					"velocità, senza che nulla venga mai conservato su un server.",
			},
			/* Italian addresses its industry corpus from its own pillar page, as Romanian, German and French do; English alone links two of them here. */
			industries: null,
		},
	},
});
